/**
 * search-suggest.js — HCS custom typo tolerance + auto-redirect
 *
 * Replaces paid apps (Searchanise / Boost / Algolia).
 * Loads on EVERY search results page (not just no-results) and
 * auto-redirects when the query matches a synonym or known typo —
 * the way Google and Amazon handle bad searches.
 *
 * Strategy:
 *   1. If query has &corrected_from=<X> param → render "Showing results
 *      for <new>. Search instead for <X>" banner. Done.
 *   2. Synonym dictionary lookup (instant). If hit → redirect.
 *   3. Levenshtein typo match (fast). If hit → redirect.
 *   4. No match → do nothing (Shopify's results stand).
 *
 * Inputs:
 *   window.HCS_SEARCH_QUERY  - the current search term (set by Liquid)
 *   #search-corrected-banner - injection point for the "showing results for X" banner
 *
 * Author: session 78 round 3 (2026-04-11). Revised after Vijay flagged
 * that the previous fire-on-no-results trigger missed real-world cases
 * because Shopify's loose substring search rarely returns exactly 0.
 */

(function () {
  'use strict';

  // ─── Synonym dictionary — HCS-specific bathroom/home-improvement vocab ───
  // Keys = what users type, values = what to search for instead.
  // Multi-word keys are matched as substrings (longest match wins).
  const SYNONYMS = {
    // Vanity synonyms
    'bathroom cabinet': 'vanity',
    'bathroom cabinets': 'vanity',
    'bathroom vanity': 'vanity',
    'bathroom vanities': 'vanity',
    'vanity cabinet': 'vanity',
    'sink cabinet': 'vanity',
    'sink base': 'vanity',
    'bath cabinet': 'vanity',
    'bath vanity': 'vanity',
    'washstand': 'vanity',
    'wash basin': 'vanity',
    'basin cabinet': 'vanity',

    // Door synonyms
    'pre hung': 'pre-hung',
    'prehung': 'pre-hung',
    'pre-hung door': 'pre-hung',
    'interior door': 'door',
    'closet door': 'sliding door',
    'french door': 'french doors',

    // Faucet synonyms
    'pot filler': 'kitchen faucet',
    'wall mount faucet': 'wall mount',
    'tap': 'faucet',
    'taps': 'faucet',
    'mixer': 'faucet',
    'mixer tap': 'faucet',
    'kitchen tap': 'kitchen faucet',
    'bathroom tap': 'bathroom faucet',

    // Flooring synonyms
    'lvp': 'vinyl',
    'l.v.p': 'vinyl',
    'luxury vinyl': 'vinyl',
    'vinyl plank': 'vinyl',
    'spc': 'vinyl',
    'waterproof floor': 'waterproof vinyl',
    'click flooring': 'laminate',
    'floating floor': 'laminate',
    'wood floor': 'engineered hardwood',
    'wood flooring': 'engineered hardwood',
    'solid wood': 'engineered hardwood',

    // Countertop synonyms
    'counter top': 'countertop',
    'worktop': 'countertop',
    'stone countertop': 'quartz',
    'stone counter': 'quartz',

    // Trim synonyms
    'molding': 'moulding',
    'moldings': 'mouldings',
    'skirting': 'baseboard',
    'skirting board': 'baseboard',
    'quarter round': 'trim',

    // Mirror synonyms
    'medicine cabinet': 'mirror',
    'lighted mirror': 'led mirror',

    // Shower synonyms
    'shower enclosure': 'shower door',
    'glass shower': 'shower door',
    'walk in shower': 'shower door',

    // Toilet synonyms
    'wc': 'toilet',
    'water closet': 'toilet',
    'loo': 'toilet',

    // General catch-alls
    'tub': 'bathtub'
  };

  // ─── Popular terms for Levenshtein fuzzy matching ───
  const POPULAR_TERMS = [
    'vanity', 'vanities', 'door', 'doors', 'flooring', 'countertop', 'countertops',
    'faucet', 'faucets', 'mirror', 'mirrors', 'shower', 'kitchen', 'bathroom',
    'hardwood', 'vinyl', 'laminate', 'quartz', 'trim', 'moulding', 'baseboard',
    'shaker', 'pre-hung', 'sliding', 'slab', 'pivot', 'frameless',
    'floating', 'freestanding', 'undermount',
    'white', 'black', 'grey', 'walnut', 'oak', 'maple', 'toilet', 'bathtub',
    'tile', 'backsplash', 'sink'
  ];

  // ─── Levenshtein edit distance ───
  function levenshtein(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(0));
    for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= b.length; j++) matrix[j][0] = j;
    for (let j = 1; j <= b.length; j++) {
      for (let i = 1; i <= a.length; i++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,
          matrix[j - 1][i] + 1,
          matrix[j - 1][i - 1] + cost
        );
      }
    }
    return matrix[b.length][a.length];
  }

  // ─── Find closest popular term (length-aware threshold) ───
  function findClosestTerm(query) {
    const q = query.toLowerCase().trim();
    if (q.length < 4) return null; // too short for confident typo match

    // If query is already a valid popular term, never suggest a substitute.
    if (POPULAR_TERMS.indexOf(q) !== -1) return null;

    // len 4    → 1 edit
    // len 5-8  → 2 edits
    // len 9+   → 3 edits
    const threshold = q.length === 4 ? 1 : q.length <= 8 ? 2 : 3;

    let best = null;
    let bestDist = Infinity;

    for (const term of POPULAR_TERMS) {
      if (Math.abs(q.length - term.length) > threshold) continue;
      const dist = levenshtein(q, term);
      if (dist > 0 && dist < bestDist && dist <= threshold) {
        bestDist = dist;
        best = term;
      }
    }
    return best;
  }

  // ─── Synonym lookup (exact + longest substring) ───
  function checkSynonyms(query) {
    const q = query.toLowerCase().trim();
    if (SYNONYMS[q]) return SYNONYMS[q];
    let match = null;
    let longest = 0;
    for (const key in SYNONYMS) {
      if (q.indexOf(key) !== -1 && key.length > longest) {
        match = SYNONYMS[key];
        longest = key.length;
      }
    }
    return match;
  }

  // ─── URL helpers ───
  function getQueryParam(name) {
    const m = location.search.match(new RegExp('[?&]' + name + '=([^&]*)'));
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : null;
  }

  function escapeHTML(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ─── Render the "Showing results for X" banner ───
  function renderCorrectedBanner(originalQuery, correctedQuery) {
    const container = document.getElementById('search-corrected-banner');
    if (!container) return;

    // Build the "search literally" URL preserving preview/tracking params.
    const preserve = ['preview_theme_id', '_fd', 'pb', '_ab', '_ck', '_pos'];
    const params = new URLSearchParams();
    params.set('q', originalQuery);
    params.set('no_correct', '1');
    for (const key of preserve) {
      const value = getQueryParam(key);
      if (value !== null) params.set(key, value);
    }
    const literalUrl = '/search?' + params.toString();

    container.innerHTML = ''
      + '<div class="search-corrected">'
      +   '<p class="search-corrected__line">'
      +     '<span class="search-corrected__label">Showing results for</span> '
      +     '<strong class="search-corrected__term">' + escapeHTML(correctedQuery) + '</strong>'
      +   '</p>'
      +   '<p class="search-corrected__line search-corrected__line--secondary">'
      +     'Search instead for '
      +     '<a class="search-corrected__link" href="' + literalUrl + '">' + escapeHTML(originalQuery) + '</a>'
      +   '</p>'
      + '</div>';
    container.hidden = false;
  }

  // ─── Auto-redirect to corrected query (preserves history with replace) ───
  // Preserves these URL params across the redirect so dev/staging previews
  // and tracking attribution survive: preview_theme_id, _fd, pb, _ab, _ck.
  function redirectTo(correctedQuery, originalQuery) {
    const preserve = ['preview_theme_id', '_fd', 'pb', '_ab', '_ck', '_pos'];
    const params = new URLSearchParams();
    params.set('q', correctedQuery);
    params.set('corrected_from', originalQuery);
    for (const key of preserve) {
      const value = getQueryParam(key);
      if (value !== null) params.set(key, value);
    }
    location.replace('/search?' + params.toString());
  }

  // ─── Phase 1: Immediate (no DOM) — auto-redirect on confident match ───
  // Runs synchronously in the <head> before the body renders. Redirects
  // happen before the wrong results paint.
  function tryRedirect() {
    const query = (window.HCS_SEARCH_QUERY || '').trim();
    if (!query) return;

    // If this page is the result of a previous correction, don't loop.
    if (getQueryParam('corrected_from')) return;

    // If user explicitly opted out, respect that.
    if (getQueryParam('no_correct') === '1') return;

    // Synonym check (instant).
    const synonym = checkSynonyms(query);
    if (synonym && synonym !== query.toLowerCase().trim()) {
      redirectTo(synonym, query);
      return;
    }

    // Levenshtein typo check (fast).
    const closest = findClosestTerm(query);
    if (closest) {
      redirectTo(closest, query);
      return;
    }
    // No correction found — let Shopify's results stand.
  }

  // ─── Phase 2: DOM-ready — render the "Showing results for X" banner ───
  // Only fires when ?corrected_from=<X> is in the URL.
  function tryRenderBanner() {
    const query = (window.HCS_SEARCH_QUERY || '').trim();
    if (!query) return;
    const correctedFrom = getQueryParam('corrected_from');
    if (!correctedFrom) return;
    renderCorrectedBanner(correctedFrom, query);
  }

  // Phase 1 runs IMMEDIATELY (before paint) for the redirect path.
  tryRedirect();

  // Phase 2 runs after DOM ready for the banner-rendering path.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryRenderBanner);
  } else {
    tryRenderBanner();
  }
})();
